import { Injectable } from '@angular/core';
import { BaseApiService } from 'projects/shared/src/lib/core/services/base-api.service';
import { Task } from 'projects/shared/src/lib/core/interfaces/task';
import { lastValueFrom } from 'rxjs';
import { TestAnswer, ProgramAnswer, NormTestAnswer } from '../interfaces/answer';
import { TaskItem } from 'projects/shared/src/lib/core/interfaces/task-item';
import { StartNewTest } from '../models/startNewTest.model';
import { PauseTask } from '../models/pauseTask.mode';
import { PlannedTestBrickGroup } from '../models/plannedTestTaskWithItems.model';

@Injectable({
  providedIn: 'root',
})
export class TaskService extends BaseApiService {
  public async getProgramTask(userProgramId: number, taskId: number, order: number): Promise<Task> {
    return await lastValueFrom(this.get<Task>(`api/v1/user-programs/${userProgramId}/tasks/${taskId}?order=${order}`));
  }

  public async getProgramTaskItem(taskName: string, itemName: string): Promise<TaskItem> {
    return await lastValueFrom(this.get<TaskItem>(`api/v1/programs/tasks/${taskName}/items/${itemName}`));
  }

  public async getTaskAnswers(taskResultId: number): Promise<TestAnswer[] | ProgramAnswer[]> {
    return await lastValueFrom(this.get<TestAnswer[] | ProgramAnswer[]>(`api/v1/tasks/answers/${taskResultId}`));
  }

  public async getTestTask(taskId: number, userTestId: number): Promise<Task> {
    return await lastValueFrom(this.get<Task>(`api/v1/tests/tasks/${taskId}?userTestId=${userTestId}`));
  }

  public async getTestTaskItem(taskId: number, itemId: number): Promise<TaskItem> {
    return await lastValueFrom(this.get<TaskItem>(`api/v1/tests/tasks/${taskId}/items/${itemId}`));
  }

  public async getNormTestTask(userNormTestId: number): Promise<Task> {
    return await lastValueFrom(this.get<Task>(`api/v1/norm-tests/task?userNormTestId=${userNormTestId}`));
  }

  public async getNormTestTaskItem(taskId: number, itemId: number): Promise<TaskItem> {
    return await lastValueFrom(this.get<TaskItem>(`api/v1/norm-tests/tasks/${taskId}/items/${itemId}`));
  }

  public async processProgramTaskAnswer(answer: ProgramAnswer): Promise<void> {
    await lastValueFrom(
      this.post<void>(`api/v1/programs/results/${answer.taskResultId}/save-answer`, answer)
    );
  }

  public async processTestTaskAnswer(answer: TestAnswer): Promise<void> {
    await lastValueFrom(
      this.post<void>(`api/v1/tests/results/${answer.taskResultId}/save-answer`, answer)
    );
  }

  public async processNormTestTaskAnswer(answer: NormTestAnswer): Promise<void> {
    await lastValueFrom(
      this.post<void>(`api/v1/norm-tests/results/${answer.taskResultId}/save-answer`, answer)
    );
  }

  public async startProgramTask(userProgramId: number, taskId: number, order: number): Promise<void> {
    await lastValueFrom(this.post<number>(`api/v1/programs/start-task`, {
      userProgramId,
      taskId,
      order
    }));
  }

  public async processTestTaskAnswers(answers: TestAnswer[]): Promise<void> {
    await lastValueFrom(
      this.post<void>(`api/v1/tests/results/save-answers`, { answers })
    );
  }

  public async processProgramTaskAnswers(answers: ProgramAnswer[]): Promise<void> {
    await lastValueFrom(
      this.post<void>(`api/v1/programs/results/save-answers`, { answers })
    );
  }

  public async processNormTestTaskAnswers(answers: NormTestAnswer[]): Promise<void> {
    await lastValueFrom(
      this.post<void>(`api/v1/norm-tests/results/save-answers`, { answers })
    );
  }

  public async startTask(model: StartNewTest): Promise<number> {
    const responseModel = await lastValueFrom(this.post<number>(`api/v1/tests/start-test`, model));
    return responseModel;
  }

  public async resumeProgramTask(taskAssignmentId: number): Promise<void> {
    await lastValueFrom(this.post<number>(`api/v1/programs/resume-task/${taskAssignmentId}`, {}));
  }

  public async resumeTestTask(taskAssignmentId: number): Promise<void> {
    await lastValueFrom(this.post<number>(`api/v1/tests/resume-task/${taskAssignmentId}`, {}));
  }

  public async pauseProgramTask(pauseTask: PauseTask): Promise<void> {
    await lastValueFrom(this.post<number>(`api/v1/programs/pause-task`, pauseTask));
  }

  public async getTestBricksWithTasks(plannedTestId: number): Promise<PlannedTestBrickGroup[]> {
    return await lastValueFrom(this.get<PlannedTestBrickGroup[]>(`api/v1/tests/plannedTests/${plannedTestId}/brick-tasks`));
  }

  public async startNormTestTask(userNormTestId: number): Promise<number> {
    return await lastValueFrom(this.post<number>(`api/v1/norm-tests/start-task`, { userNormTestId }));
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
